import { ENTERPRISE_URL } from "./scenarioNav";

export type AuthSnapshot = {
  isLoading: boolean;
  isSignedIn: boolean;
  organizationId: string | null;
};

export type RouteDecision =
  | { kind: 'loading' }
  | { kind: 'redirect'; to: string; logWelcome?: boolean }
  | { kind: 'render' };

// Single source of truth for no-org_id routing. Pure: no React, no SDK, no I/O.
export function resolveProtectedRoute(auth: AuthSnapshot, pathname: string): RouteDecision {
  if (auth.isLoading) return { kind: 'loading' };
  if (!auth.isSignedIn) return { kind: 'redirect', to: '/' };
  const onWelcome = pathname === '/welcome';
  if (auth.organizationId && onWelcome) return { kind: 'redirect', to: ENTERPRISE_URL };
  if (!auth.organizationId && !onWelcome) return { kind: 'redirect', to: '/welcome', logWelcome: true };
  return { kind: 'render' };
}

// The client-side EULA gate (EIT-140). 'pending' = terms state not loaded yet (hold a loader);
// 'error' = the /me terms read failed (fail-closed, do not render the Hub); 'gate' = redirect the
// admin to /terms; 'render' = let them through. Only org-admins are ever gated — members render
// straight away. Routing scopes this to the Hub subtree, so no pathname is needed here.
export type TermsState = 'pending' | 'error' | 'accepted' | 'not-accepted';
export type TermsGateDecision = 'render' | 'pending' | 'error' | 'gate';

export function resolveTermsGate(isOrgAdmin: boolean, terms: TermsState): TermsGateDecision {
  if (!isOrgAdmin) return 'render';
  switch (terms) {
    case 'pending': return 'pending';
    case 'error': return 'error';
    case 'accepted': return 'render';
    case 'not-accepted': return 'gate';
  }
}

// Where an already-signed-in visitor to Landing should go. Callers MUST only
// invoke this once auth has settled (isLoading === false).
export function resolveLandingRedirect(organizationId: string | null): string {
  return organizationId ? ENTERPRISE_URL : '/welcome';
}

// Post-signin destination. 'pending' while auth resolves — organizationId is
// null/unsettled on first render and MUST NOT be treated as "zero-org" yet.
export function resolvePostSigninDestination(
  auth: { isLoading: boolean; organizationId: string | null },
): 'pending' | 'welcome' | 'resolve' {
  if (auth.isLoading) return 'pending';
  return auth.organizationId ? 'resolve' : 'welcome';
}

// Production hub/slug hosts are exactly `<label>.cloudhouse.com` (3 labels). Local dev
// hosts (`localhost`, 4-label `hub.localhost.cloudhouse.com`) deliberately do NOT match,
// so the bounce never fires in dev.
const CLOUDHOUSE_HOST_RE = /^([a-z0-9-]+)\.cloudhouse\.com$/;
const NEUTRAL_SLUG = 'hub';

// True only for production hub/slug hosts (`<label>.cloudhouse.com`); dev hosts do not match.
export function isCloudhouseHost(hostname: string): boolean {
  return CLOUDHOUSE_HOST_RE.test(hostname);
}

export type HostRedirect = { to: string; reason: 'wrong_slug' | 'hub_to_slug' | 'slug_to_hub' };

// Bounce a signed-in user to the right host. Pure: host + claim in, decision out. A slug
// destination is built ONLY from the server-issued org_slug claim, never the URL slug — a
// spoofed URL cannot redirect anywhere but the user's own host. Returns null (render here)
// only for a non-production host, or when already on the right one.
export function resolveHostRedirect(
  hostname: string,
  orgSlug: string | null,
  pathname = '',
  search = '',
): HostRedirect | null {
  const match = CLOUDHOUSE_HOST_RE.exec(hostname);
  if (!match) return null; // dev / non-cloudhouse host: never bounce
  const urlSlug = match[1];

  // No verified org_slug yet (zero-org, or a token issued before the claim ships): the user
  // must not sit on a specific org's host we can't attribute to them. Send them to the
  // neutral hub host, which serves every org. Already on hub → render here.
  if (!orgSlug) {
    if (urlSlug === NEUTRAL_SLUG) return null;
    return { to: `https://${NEUTRAL_SLUG}.cloudhouse.com${pathname}${search}`, reason: 'slug_to_hub' };
  }

  const targetHost = `${orgSlug}.cloudhouse.com`;
  // Never bounce to the host we're already on — guards a mixed-case claim or a
  // reserved-slug collision from becoming an infinite cross-origin redirect loop.
  if (targetHost.toLowerCase() === hostname.toLowerCase()) return null;
  const to = `https://${targetHost}${pathname}${search}`;
  return { to, reason: urlSlug === NEUTRAL_SLUG ? 'hub_to_slug' : 'wrong_slug' };
}
